import type { AnyBlock } from "@/types";

/** CD "Terms & conditions" — a native <details> accordion (default open)
 *  wrapping a 2-column grid of headed term sections. Fixed-layout designed
 *  section; content comes from props (CD defaults below). */
interface CdTermsSection {
  heading: string;
  note?: string;
  paragraphs?: string[];
  list?: string[];
}
interface CdTermsCell {
  sections: CdTermsSection[];
}
interface CdTermsProps {
  label?: string;
  items?: CdTermsCell[];
}

const DEFAULTS: { label: string; items: CdTermsCell[] } = {
  label: "Terms & conditions",
  items: [
    {
      sections: [
        {
          heading: "Fees & payment terms",
          paragraphs: [
            "Our fees shown above include VAT. Unless stated otherwise, our fee does not include additional services or fees due to other consultants or bodies such as the local authority, printing, travel, unusual postal or courier expenses, or physical / 3D computer models.",
            "Payment terms — Planning drawings: 50% on completion of the survey, balance on completion of the drawings. Building regulation drawings: 50% on commencement, balance on completion. 3D imagery: 100% on completion of the survey. Payment is due within 7 days of the invoice issue date; we require payment for each stage before starting the next.",
          ],
        },
        {
          heading: "Drawings",
          paragraphs: [
            "Our drawings and specifications should not be used for construction unless or until they have specifically been issued for that purpose by us. Electronic copies of drawings will be issued in PDF format only.",
          ],
        },
      ],
    },
    {
      sections: [
        {
          heading: "Amendments",
          paragraphs: [
            "Amendments are free during the design stage as long as they remain within the original brief, as are any amendments requested by the planners in order to obtain permission. We reserve the right to charge at our standard hourly rate for a redesign or major amendments outside the original brief, amendments following approval, alterations to account for builder changes on site, or additional site visits. We will agree any extra charges with you in advance.",
          ],
        },
        {
          heading: "Liability",
          paragraphs: [
            "We accept no responsibility for building components, fixtures or fittings; their fitness for purpose rests with the manufacturer or supplier. Where work is carried out on existing buildings we cannot guarantee the existing structure is sound or can be made damp-proof, nor can we be held responsible for concealed or underground elements. Our total liability in connection with any commission shall not exceed the final fee as paid.",
          ],
        },
      ],
    },
    {
      sections: [
        {
          heading: "Additional services we can offer",
          note: "Fee and terms to be agreed separately.",
          list: [
            "Interior design, detailed design of furniture or fittings, e.g. kitchen cabinets.",
            "Detailing special or unusual finishes.",
            "Preparing 3D computer models, and artist's impressions / perspectives.",
            "‘As built’ drawings, or drawings for publicity purposes.",
          ],
        },
      ],
    },
    {
      sections: [
        {
          heading: "Services from other consultants, appointed separately",
          note: "Your project may require the support of additional technical experts. Examples include:",
          list: [
            "Topographical / levels survey.",
            "Structural, mechanical or electrical design and engineering.",
            "Plumbing and heating design.",
            "Landscape or furniture design.",
            "Cost advice, or advice relating to rights of light, access, support, boundaries, rights of way, covenants, party walls, etc.",
            "Any reports or specialist information required by the local authority, e.g. tree or wildlife surveys, planning statements, SAP energy ratings, flood risk assessments, and ecology reports.",
          ],
        },
      ],
    },
  ],
};

export function CdTermsBlock({ block }: { block: AnyBlock }) {
  const p = (block.props ?? {}) as CdTermsProps;
  const items = p.items ?? DEFAULTS.items;

  return (
    <section className="block cd-terms">
      <details className="cd-terms__acc">
        <summary className="cd-terms__summary">
          <span className="eyebrow">{p.label ?? DEFAULTS.label}</span>
          <span className="cd-terms__chev" aria-hidden="true" />
        </summary>
        <div className="cd-terms__grid">
          {items.map((cell, i) => (
            <div className="cd-terms__cell" key={i}>
              {cell.sections.map((s, j) => (
                <div className="cd-terms__section" key={j}>
                  <h4 className="cd-terms__h">{s.heading}</h4>
                  {s.note ? <p className="small cd-terms__note">{s.note}</p> : null}
                  {(s.paragraphs ?? []).map((para, k) => (
                    <p className="cd-terms__p" key={k}>{para}</p>
                  ))}
                  {s.list ? (
                    <ul className="cd-terms__list">
                      {s.list.map((li, k) => (
                        <li key={k}>{li}</li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ))}
            </div>
          ))}
        </div>
      </details>
    </section>
  );
}
